import { useEffect, useState } from 'react';
import {
  Upload,
  TrendingUp,
  TrendingDown,
  Bitcoin,
  BarChart3,
  Loader2,
  Calculator,
  Download,
} from 'lucide-react';
import { useTaxStore } from '@/store/taxStore';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';

const cryptoExchanges = [
  { value: 'gemini', label: 'Gemini' },
  { value: 'kraken', label: 'Kraken' },
  { value: 'trust_wallet', label: 'Trust Wallet' },
  { value: 'coinbase', label: 'Coinbase' },
  { value: 'other', label: 'Other' },
];

const brokerLabels: Record<string, string> = {
  fidelity: 'Fidelity',
  schwab: 'Charles Schwab',
  robinhood: 'Robinhood',
  etrade: 'E*TRADE',
  td_ameritrade: 'TD Ameritrade',
  vanguard: 'Vanguard',
  webull: 'Webull',
  interactive_brokers: 'Interactive Brokers',
  coinbase: 'Coinbase',
  kraken: 'Kraken',
  other: 'Other',
};

export function InvestmentsPage() {
  const { selectedYear, investments, fetchInvestments, isLoading } = useTaxStore();
  const [activeTab, setActiveTab] = useState<'crypto' | 'stocks'>('crypto');
  const [showImport, setShowImport] = useState(false);
  const [importType, setImportType] = useState<'crypto' | 'stocks'>('crypto');
  const [exchange, setExchange] = useState('gemini');
  const [broker, setBroker] = useState('fidelity');
  const [csvContent, setCsvContent] = useState('');
  const [importing, setImporting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [gains, setGains] = useState<any>(null);

  useEffect(() => {
    fetchInvestments(selectedYear);
  }, [selectedYear, fetchInvestments]);

  const handleImport = async () => {
    if (!csvContent.trim()) return;
    setImporting(true);
    try {
      if (importType === 'crypto') {
        await api.importCrypto({
          exchange,
          year: selectedYear,
          csvData: csvContent,
        });
      } else {
        await api.importStocks({
          broker,
          year: selectedYear,
          csvData: csvContent,
        });
      }
      fetchInvestments(selectedYear);
      setShowImport(false);
      setCsvContent('');
    } catch (err) {
      console.error('Import error:', err);
    }
    setImporting(false);
  };

  const calculateGains = async () => {
    setCalculating(true);
    try {
      const result = await api.calculateGains(selectedYear, 'fifo');
      setGains(result);
    } catch (err) {
      console.error('Calculate error:', err);
    }
    setCalculating(false);
  };

  const cryptoInvestments = investments.filter((i: any) => i.type === 'crypto');
  const stockInvestments = investments.filter((i: any) => i.type === 'stock');

  // Stock sales summary grouped by broker
  const stockSales = stockInvestments.filter((i: any) => i.transactionType === 'sell');
  const stockDividends = stockInvestments.filter((i: any) => i.transactionType === 'dividend');
  const stockSalesByBroker: Record<string, { count: number; total: number }> = {};
  stockSales.forEach((s: any) => {
    const broker = s.broker || 'Unknown';
    if (!stockSalesByBroker[broker]) stockSalesByBroker[broker] = { count: 0, total: 0 };
    stockSalesByBroker[broker].count++;
    stockSalesByBroker[broker].total += s.totalValue || 0;
  });
  const totalStockSalesAmount = stockSales.reduce((sum: number, s: any) => sum + (s.totalValue || 0), 0);
  const totalDividendsAmount = stockDividends.reduce((sum: number, s: any) => sum + (s.totalValue || 0), 0);

  // Overall totals across all investment types
  const allSalesAll = investments.filter((i: any) => i.transactionType === 'sell');
  const totalProceeds = allSalesAll.reduce((sum: number, s: any) => sum + (s.totalValue || 0), 0);
  const totalCostBasis = allSalesAll.reduce((sum: number, s: any) => sum + (s.costBasis || 0), 0);
  const totalGainLoss = allSalesAll.reduce((sum: number, s: any) => {
    if (s.costBasis && s.costBasis > 0) return sum + ((s.totalValue || 0) - s.costBasis);
    if (s.gainLoss !== undefined) return sum + s.gainLoss;
    return sum;
  }, 0);

  const downloadForm8949 = () => {
    // Group sales by broker for separate 8949 forms
    const salesByBroker: Record<string, any[]> = {};
    const allSales = [...stockSales, ...cryptoInvestments.filter((i: any) => i.transactionType === 'sell')];
    allSales.forEach((s: any) => {
      const b = s.broker || s.exchange || 'Unknown';
      if (!salesByBroker[b]) salesByBroker[b] = [];
      salesByBroker[b].push(s);
    });

    const csvLines: string[] = [];
    csvLines.push('Form 8949 - Sales and Other Dispositions of Capital Assets');
    csvLines.push(`Tax Year: ${selectedYear}`);
    csvLines.push('');

    Object.entries(salesByBroker).forEach(([brokerKey, sales]) => {
      const label = brokerLabels[brokerKey] || brokerKey;
      csvLines.push(`1099 Provider: ${label}`);
      csvLines.push('(a) Description of property,(b) Date acquired,(c) Date sold or disposed of,(d) Proceeds,(e) Cost or other basis,(f) Code (see instructions),(g) Adjustment,(h) Gain or (loss)');

      let totalProceeds = 0;
      let totalCostBasis = 0;

      sales.forEach((s: any) => {
        const symbol = s.symbol || s.asset || '';
        const shares = s.shares || s.amount || 0;
        const desc = `${shares} ${symbol}`;
        const dateSold = s.date ? new Date(s.date).toLocaleDateString('en-US') : '';
        const proceeds = s.totalValue || 0;
        const costBasis = s.costBasis || 0;
        const gainLoss = costBasis > 0 ? proceeds - costBasis : '';

        totalProceeds += proceeds;
        totalCostBasis += costBasis;

        csvLines.push(
          [
            `"${desc}"`,
            '"Various"',
            `"${dateSold}"`,
            proceeds.toFixed(2),
            costBasis > 0 ? costBasis.toFixed(2) : '',
            '',
            '',
            typeof gainLoss === 'number' ? gainLoss.toFixed(2) : '',
          ].join(',')
        );
      });

      csvLines.push(
        [
          '"Totals"',
          '',
          '',
          totalProceeds.toFixed(2),
          totalCostBasis > 0 ? totalCostBasis.toFixed(2) : '',
          '',
          '',
          totalCostBasis > 0 ? (totalProceeds - totalCostBasis).toFixed(2) : '',
        ].join(',')
      );
      csvLines.push('');
    });

    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Form_8949_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Investments</h2>
          <p className="text-muted-foreground">Track crypto and stock gains/losses</p>
        </div>
        <div className="flex gap-2">
          {investments.length > 0 && (
            <button
              onClick={downloadForm8949}
              className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
            >
              <Download className="h-4 w-4" />
              Form 8949
            </button>
          )}
          <button
            onClick={calculateGains}
            disabled={calculating}
            className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
          >
            {calculating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Calculator className="h-4 w-4" />
            )}
            Calculate Gains
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
          >
            <Upload className="h-4 w-4" />
            Import CSV
          </button>
        </div>
      </div>

      {/* Overall Summary */}
      {allSalesAll.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground mb-1">Total Proceeds</p>
            <p className="text-xl font-bold">{formatCurrency(totalProceeds)}</p>
            <p className="text-xs text-muted-foreground">{allSalesAll.length} sale{allSalesAll.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground mb-1">Total Cost Basis</p>
            <p className="text-xl font-bold">
              {totalCostBasis > 0 ? formatCurrency(totalCostBasis) : 'Not provided'}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground mb-1">Total Gain/Loss</p>
            <p className={cn(
              'text-xl font-bold',
              totalGainLoss >= 0 ? 'text-green-500' : 'text-red-500'
            )}>
              {totalCostBasis > 0 || totalGainLoss !== 0 ? formatCurrency(totalGainLoss) : 'N/A'}
            </p>
          </div>
        </div>
      )}

      {/* Calculated Gains Summary */}
      {gains && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground mb-1">Short-Term Gains</p>
            <p className={cn(
              'text-xl font-bold',
              gains.combined.shortTermGains - gains.combined.shortTermLosses >= 0
                ? 'text-green-500'
                : 'text-red-500'
            )}>
              {formatCurrency(gains.combined.shortTermGains - gains.combined.shortTermLosses)}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground mb-1">Long-Term Gains</p>
            <p className={cn(
              'text-xl font-bold',
              gains.combined.longTermGains - gains.combined.longTermLosses >= 0
                ? 'text-green-500'
                : 'text-red-500'
            )}>
              {formatCurrency(gains.combined.longTermGains - gains.combined.longTermLosses)}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground mb-1">Wash Sales Disallowed</p>
            <p className="text-xl font-bold text-yellow-500">
              {formatCurrency(gains.combined.washSalesDisallowed)}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('crypto')}
          className={cn(
            'px-4 py-2 font-medium border-b-2 transition-smooth -mb-px',
            activeTab === 'crypto'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          <Bitcoin className="h-4 w-4 inline mr-2" />
          Crypto ({cryptoInvestments.length})
        </button>
        <button
          onClick={() => setActiveTab('stocks')}
          className={cn(
            'px-4 py-2 font-medium border-b-2 transition-smooth -mb-px',
            activeTab === 'stocks'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          <BarChart3 className="h-4 w-4 inline mr-2" />
          Stocks ({stockInvestments.length})
        </button>
      </div>

      {/* Stock Sales Summary */}
      {activeTab === 'stocks' && stockSales.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground mb-1">Total Sales</p>
            <p className="text-xl font-bold">{stockSales.length}</p>
            <p className="text-lg font-semibold text-red-500">{formatCurrency(totalStockSalesAmount)}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground mb-1">Total Dividends</p>
            <p className="text-xl font-bold">{stockDividends.length}</p>
            <p className="text-lg font-semibold text-green-500">{formatCurrency(totalDividendsAmount)}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground mb-2">By Provider</p>
            {Object.entries(stockSalesByBroker).map(([broker, data]) => (
              <div key={broker} className="flex justify-between items-center py-1">
                <span className="text-sm font-medium">{brokerLabels[broker] || broker}</span>
                <span className="text-sm text-muted-foreground">
                  {data.count} sale{data.count !== 1 ? 's' : ''} &middot; {formatCurrency(data.total)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Investments List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Type</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Asset</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Amount</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Proceeds</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Cost Basis</th>
                <th className="text-right p-4 text-sm font-medium text-muted-foreground">Gain/Loss</th>
                <th className="text-left p-4 text-sm font-medium text-muted-foreground">Term</th>
              </tr>
            </thead>
            <tbody>
              {(activeTab === 'crypto' ? cryptoInvestments : stockInvestments).map((inv: any) => (
                <tr key={inv.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                  <td className="p-4 text-sm">{new Date(inv.date).toLocaleDateString()}</td>
                  <td className="p-4">
                    <span className={cn(
                      'px-2 py-1 rounded text-xs font-medium',
                      inv.transactionType === 'buy'
                        ? 'bg-green-500/10 text-green-500'
                        : inv.transactionType === 'sell'
                        ? 'bg-red-500/10 text-red-500'
                        : 'bg-blue-500/10 text-blue-500'
                    )}>
                      {inv.transactionType}
                    </span>
                  </td>
                  <td className="p-4 text-sm font-medium">{inv.asset || inv.symbol}</td>
                  <td className="p-4 text-sm text-right">{(inv.amount || inv.shares)?.toFixed(4)}</td>
                  <td className="p-4 text-sm text-right">{formatCurrency(inv.totalValue || 0)}</td>
                  <td className="p-4 text-sm text-right">
                    {inv.costBasis && inv.costBasis > 0
                      ? formatCurrency(inv.costBasis)
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-4 text-sm text-right">
                    {inv.costBasis && inv.costBasis > 0 ? (
                      <span className={(inv.totalValue || 0) - inv.costBasis >= 0 ? 'text-green-500' : 'text-red-500'}>
                        {formatCurrency((inv.totalValue || 0) - inv.costBasis)}
                      </span>
                    ) : inv.gainLoss !== undefined ? (
                      <span className={inv.gainLoss >= 0 ? 'text-green-500' : 'text-red-500'}>
                        {formatCurrency(inv.gainLoss)}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-4 text-sm">
                    {inv.transactionType === 'sell' && (
                      <span className={cn(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        inv.holdingPeriod === 'long'
                          ? 'bg-purple-500/10 text-purple-500'
                          : 'bg-orange-500/10 text-orange-500'
                      )}>
                        {inv.holdingPeriod === 'long' ? 'Long' : 'Short'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {(activeTab === 'crypto' ? cryptoInvestments : stockInvestments).length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <p>No {activeTab} transactions found</p>
              <p className="text-sm">Import a CSV to get started</p>
            </div>
          )}
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4">Import Transactions</h3>

            <div className="space-y-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setImportType('crypto')}
                  className={cn(
                    'flex-1 py-2 rounded-lg font-medium transition-smooth',
                    importType === 'crypto'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  Crypto
                </button>
                <button
                  onClick={() => setImportType('stocks')}
                  className={cn(
                    'flex-1 py-2 rounded-lg font-medium transition-smooth',
                    importType === 'stocks'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  Stocks
                </button>
              </div>

              {importType === 'crypto' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Exchange</label>
                  <select
                    value={exchange}
                    onChange={(e) => setExchange(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  >
                    {cryptoExchanges.map((ex) => (
                      <option key={ex.value} value={ex.value}>{ex.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {importType === 'stocks' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Broker / 1099 Provider</label>
                  <select
                    value={broker}
                    onChange={(e) => setBroker(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                  >
                    <option value="fidelity">Fidelity</option>
                    <option value="schwab">Charles Schwab</option>
                    <option value="robinhood">Robinhood</option>
                    <option value="etrade">E*TRADE</option>
                    <option value="td_ameritrade">TD Ameritrade</option>
                    <option value="vanguard">Vanguard</option>
                    <option value="webull">Webull</option>
                    <option value="interactive_brokers">Interactive Brokers</option>
                    <option value="coinbase">Coinbase (1099-DA)</option>
                    <option value="kraken">Kraken (1099-DA)</option>
                    <option value="other">Other</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Re-importing for the same broker/year replaces previous data
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  CSV Data
                </label>
                <textarea
                  value={csvContent}
                  onChange={(e) => setCsvContent(e.target.value)}
                  className="w-full h-40 px-3 py-2 bg-background border border-border rounded-lg font-mono text-sm"
                  placeholder="Paste your CSV content here..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowImport(false)}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing || !csvContent.trim()}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
                >
                  {importing ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
